import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { MeasurementSettings, ScriptCheck, SettingsDomain, SettingsResponse } from "../../shared/types";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";
import { readPaste } from "../lib/script-paste";
import { shownHost } from "./parts";
import { TRACK_NAMES, type TrackKind, trackProblem } from "./rules";
import { useSetup } from "./SetupContext";
import { type TrackPart, trackParts } from "./trackParts";
import type { Run } from "./useSettingsData";

/** The address that opens the main room's sign-up page, once one is published. */
function signupPage(d: SettingsDomain): string | null {
  return d.published_webinars.length ? `https://${shownHost(d)}/` : null;
}

function checkFor(kind: TrackKind, m: MeasurementSettings, scriptId: string | null): ScriptCheck["state"] {
  if (kind === "meta") return m.checks.pixel.state;
  if (kind === "ga") return m.checks.ga.state;
  return m.scripts.find((s) => s.id === scriptId)?.check.state ?? "wait";
}

export type CardState = "none" | "bad" | "checking" | "loaded" | "waiting";

export interface Card {
  on: boolean;
  value: string;
  token: string;
  st: CardState;
}

const POLL_MS = 3000;
const POLL_TRIES = 20;


/**
 * The three tools as being set up: each one's ID, whether it is on and whether it has been seen loading. An ID is
 * checked for its shape before anything counts as loaded. `opened` is a tool to start switched on.
 */
export function useTrackWork(data: SettingsResponse, run: Run, setData: (d: SettingsResponse) => void, opened?: TrackKind) {
  const navigate = useNavigate();
  const toast = useToast();
  const { set } = useSetup();
  const m = data.measurement;
  const script = m.scripts[0] ?? null;
  const parts = trackParts(data);
  const initial = (k: TrackKind): Card => {
    const p = k === "own" ? parts.find((x) => x.id === script?.id) : parts.find((x) => x.kind === k);
    const value = k === "meta" ? m.meta_pixel_id ?? "" : k === "ga" ? m.ga_measurement_id ?? "" : script?.code ?? "";
    return { on: !!p?.on || opened === k, value, token: "", st: p?.state === "loaded" ? "loaded" : "none" };
  };
  const [cards, setCards] = useState<Record<TrackKind, Card>>(() => ({ meta: initial("meta"), ga: initial("ga"), own: initial("own") }));
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );
  const patch = (k: TrackKind, p: Partial<Card>) => setCards((c) => ({ ...c, [k]: { ...c[k], ...p } }));

  const saveOne = async (k: TrackKind, c: Card): Promise<string | null | false> => {
    try {
      if (k === "meta") {
        const d = await api.settings.measurement({ meta_pixel_id: c.value.replace(/\s/g, ""), meta_on: true, ...(c.token.trim() ? { meta_capi_token: c.token.trim() } : {}) });
        setData(d);
        return null;
      }
      if (k === "ga") {
        setData(await api.settings.measurement({ ga_measurement_id: c.value.trim().toUpperCase(), ga_on: true }));
        return null;
      }
      const reading = readPaste(c.value);
      const name = reading.kind === "script" && reading.name ? reading.name : TRACK_NAMES.own;
      const d = script ? await api.settings.updateScript(script.id, { code: c.value.trim(), on: true }) : await api.settings.addScript({ name, code: c.value.trim() });
      setData(d);
      return script?.id ?? d.measurement.scripts.find((s) => s.code === c.value.trim())?.id ?? null;
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "Something went wrong.", icon: "info" });
      return false;
    }
  };

  const checkLoads = async (k: TrackKind) => {
    const c = cards[k];
    if (trackProblem(k, c.value)) return patch(k, { st: "bad" });
    const page = signupPage(data.domain);
    // Opened at the press, before anything waits, so the browser lets the tab open.
    const tab = page ? window.open("", "_blank") : null;
    patch(k, { st: "checking" });
    const saved = await saveOne(k, c);
    if (saved === false) {
      tab?.close();
      return patch(k, { st: "none" });
    }
    if (!tab || !page) return patch(k, { st: "waiting" });
    tab.opener = null;
    tab.location.href = page;
    for (let i = 0; i < POLL_TRIES && alive.current; i++) {
      await new Promise((r) => window.setTimeout(r, POLL_MS));
      if (!alive.current) return;
      const d = await api.settings.get().catch(() => null);
      if (!d) continue;
      setData(d);
      if (checkFor(k, d.measurement, saved) === "good") {
        patch(k, { st: "loaded" });
        toast(`${TRACK_NAMES[k]} loaded`);
        return;
      }
    }
    if (alive.current) patch(k, { st: "waiting" });
  };

  const finish = async () => {
    const bad = (Object.keys(cards) as TrackKind[]).filter((k) => cards[k].on && trackProblem(k, cards[k].value));
    if (bad.length) {
      setCards((c) => {
        const next = { ...c };
        for (const k of bad) next[k] = { ...next[k], st: "bad" };
        return next;
      });
      toast({ text: `${TRACK_NAMES[bad[0]]}: ${trackProblem(bad[0], cards[bad[0]].value)}`, icon: "info" });
      return;
    }
    setBusy(true);
    const { meta, ga, own } = cards;
    const ok = await run(() =>
      api.settings.measurement({
        meta_on: meta.on,
        ga_on: ga.on,
        ...(meta.on ? { meta_pixel_id: meta.value.replace(/\s/g, "") } : {}),
        ...(meta.on && meta.token.trim() ? { meta_capi_token: meta.token.trim() } : {}),
        ...(ga.on ? { ga_measurement_id: ga.value.trim().toUpperCase() } : {}),
      }),
    );
    const scriptOk = !ok
      ? false
      : own.on
        ? (await saveOne("own", own)) !== false
        : script?.on
          ? await run(() => api.settings.updateScript(script.id, { on: false }))
          : true;
    if (!ok || !scriptOk) return setBusy(false);
    await markDone();
  };

  const skip = async () => {
    setBusy(true);
    const ok = await run(() => api.settings.measurement({ meta_on: false, ga_on: false }));
    for (const s of m.scripts.filter((x) => x.on)) await run(() => api.settings.updateScript(s.id, { on: false }));
    if (ok) await markDone();
    else setBusy(false);
  };

  const markDone = async () => {
    try {
      set(await api.settings.markSetup({ step: "tracking" }));
      navigate("/setup/ready");
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "Something went wrong.", icon: "info" });
      setBusy(false);
    }
  };

  return { cards, patch, busy, saveOne, checkLoads, finish, skip, tokenSaved: m.meta_token_saved };
}

/** A tool's switch: on or off at once. False when it has no ID yet, so there is nothing to switch and its ID is asked for. */
export function flipTrack(part: TrackPart, run: Run): boolean {
  if (!part.value) return false;
  if (part.kind === "meta") void run(() => api.settings.measurement({ meta_on: !part.on }), part.on ? "Meta pixel is off" : "Meta pixel is on");
  else if (part.kind === "ga") void run(() => api.settings.measurement({ ga_on: !part.on }), part.on ? "Google Analytics is off" : "Google Analytics is on");
  else if (part.id) {
    const id = part.id;
    void run(() => api.settings.updateScript(id, { on: !part.on }), part.on ? `${part.name} is off` : `${part.name} is on`);
  }
  return true;
}
